import { useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { BulkBar, RowActions, RowCheck, useBulkSelect } from "@/components/AdminBulkActions";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PARTNER_ROLE_OPTIONS, ROLE_LABELS } from "@/lib/constants";
import { PRODUCT_CATALOG, PRODUCT_ORDER } from "@/lib/productCatalog";
import { UserPlus, Upload, KeyRound, CheckCircle2, Loader2, FileCheck, Boxes, LayoutDashboard } from "lucide-react";
import { DashboardToolsDialog } from "@/components/DashboardToolsDialog";

const EMPTY = { email: "", password: "", name: "", ragione_sociale: "", tipo_collab: "A_A",
  ruolo_rete: "partner_broker", rui_section: "", rui_number: "",
  mup_partner: { ragione_sociale: "", partita_iva: "", pec: "", responsabile: "", indirizzo: "", recapiti: "", cell: "", sito: "" } };

const STATO_META = {
  attivo: { label: "Attivo", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  sospeso: { label: "Sospeso", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  cancellato: { label: "Cancellato", cls: "bg-red-50 text-red-700 border-red-200" },
  in_attesa_documenti: { label: "In attesa documenti", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};
const STATO_ORDER = ["attivo", "in_attesa_documenti", "sospeso", "cancellato"];

export default function Partners() {
  const [partners, setPartners] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [mandatoFile, setMandatoFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [resetFor, setResetFor] = useState(null);
  const [newPwd, setNewPwd] = useState("");
  const [prodFor, setProdFor] = useState(null);
  const [prodSel, setProdSel] = useState([]);
  const [prodSaving, setProdSaving] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [dashFor, setDashFor] = useState(null);
  const sel = useBulkSelect();
  const fileRef = useRef();

  const load = () => api.get(`/admin/partners${showArchived ? "?include_archived=true" : ""}`).then(({ data }) => { setPartners(data); sel.clear(); });
  useEffect(() => { load(); }, [showArchived]); // eslint-disable-line

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setMup = (k) => (e) => setForm({ ...form, mup_partner: { ...form.mup_partner, [k]: e.target.value } });

  const create = async () => {
    setSaving(true);
    try {
      let mandato_file_id = null;
      if (mandatoFile) {
        const fd = new FormData();
        fd.append("file", mandatoFile);
        fd.append("tipo", "mandato");
        const { data } = await api.post("/upload", fd);
        mandato_file_id = data.id;
      }
      await api.post("/admin/partners", { ...form, mandato_file_id,
        mup_partner: { ...form.mup_partner, ragione_sociale: form.mup_partner.ragione_sociale || form.ragione_sociale } });
      toast.success("Partner creato e attivato");
      setOpen(false); setForm(EMPTY); setMandatoFile(null); load();
    } catch (e) {
      toast.error(apiError(e));
    } finally { setSaving(false); }
  };

  const doReset = async () => {
    try {
      await api.post(`/admin/partners/${resetFor.id}/reset-password`, { new_password: newPwd });
      toast.success("Password aggiornata");
      setResetFor(null); setNewPwd("");
    } catch (e) { toast.error(apiError(e)); }
  };

  const changeStato = async (p, stato) => {
    const s = partners.find((x) => x.id === p.id)?.stato || "attivo";
    if (stato === s) return;
    try {
      await api.post(`/admin/partners/${p.id}/stato`, { stato });
      toast.success(stato === "attivo"
        ? `${p.ragione_sociale} riattivato`
        : `${p.ragione_sociale}: ${STATO_META[stato].label} — accesso alla piattaforma bloccato`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleIncasso = async (p) => {
    try {
      await api.put(`/admin/partners/${p.id}`, { incasso_diretto: !p.incasso_diretto });
      toast.success(!p.incasso_diretto
        ? `${p.ragione_sociale}: incasso diretto ATTIVO (il premio è versato al Partner)`
        : `${p.ragione_sociale}: incasso diretto disattivato (il premio è versato all'Agenzia)`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const openProd = (p) => { setProdSel(Array.isArray(p.enabled_products) ? p.enabled_products : PRODUCT_ORDER); setProdFor(p); };
  const toggleProd = (code) => setProdSel((s) => s.includes(code) ? s.filter((c) => c !== code) : [...s, code]);
  const saveProd = async () => {
    setProdSaving(true);
    try {
      await api.put(`/admin/partners/${prodFor.id}`, { enabled_products: prodSel });
      toast.success(`Prodotti aggiornati per ${prodFor.ragione_sociale}`);
      setProdFor(null); load();
    } catch (e) { toast.error(apiError(e)); } finally { setProdSaving(false); }
  };


  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-semibold text-forest">Onboarding partner</h1>
          <p className="mt-1 text-muted-foreground">Accesso su mandato firmato — nessuna auto-registrazione.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-partner-button" className="gap-2 rounded-lg bg-terracotta hover:bg-terracotta-hover">
              <UserPlus className="h-4 w-4" /> Nuovo partner
            </Button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
            <DialogHeader><DialogTitle className="font-heading text-forest">Nuovo partner (admin-driven)</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Nome referente *</Label><Input data-testid="p-name" value={form.name} onChange={set("name")} /></div>
                <div className="space-y-1.5"><Label>Ragione sociale *</Label><Input data-testid="p-ragione" value={form.ragione_sociale} onChange={set("ragione_sociale")} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Email (login) *</Label><Input data-testid="p-email" type="email" value={form.email} onChange={set("email")} /></div>
                <div className="space-y-1.5"><Label>Password iniziale *</Label><Input data-testid="p-password" value={form.password} onChange={set("password")} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Tipo collaborazione *</Label>
                  <Select value={form.tipo_collab} onValueChange={(v) => setForm({ ...form, tipo_collab: v })}>
                    <SelectTrigger data-testid="p-tipo"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="A_A">A con A (Agenzia)</SelectItem>
                      <SelectItem value="A_B">A con B (Broker)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Ruolo di rete *</Label>
                  <Select value={form.ruolo_rete} onValueChange={(v) => setForm({ ...form, ruolo_rete: v })}>
                    <SelectTrigger data-testid="p-ruolo"><SelectValue /></SelectTrigger>
                    <SelectContent>{PARTNER_ROLE_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Sezione RUI *</Label><Input data-testid="p-rui-section" value={form.rui_section} onChange={set("rui_section")} placeholder="A / B / E" /></div>
                <div className="space-y-1.5"><Label>Numero RUI *</Label><Input data-testid="p-rui-number" value={form.rui_number} onChange={set("rui_number")} /></div>
              </div>
              <div className="rounded-lg border border-border bg-sage/40 p-3">
                <Label className="text-xs font-semibold uppercase tracking-wide text-forest">Dati collaborazione partner (MUP de-brandizzato)</Label>
                <p className="mt-1 text-[11px] text-muted-foreground">Raccolti all'attivazione, a valle del mandato firmato. Confluiscono nel MUP precompilato e nelle proposte.</p>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <Input data-testid="p-piva" placeholder="Partita IVA" value={form.mup_partner.partita_iva} onChange={setMup("partita_iva")} />
                  <Input data-testid="p-pec" placeholder="PEC" value={form.mup_partner.pec} onChange={setMup("pec")} />
                  <Input data-testid="p-responsabile" placeholder="Responsabile attività di intermediazione" value={form.mup_partner.responsabile} onChange={setMup("responsabile")} />
                  <Input data-testid="p-cell" placeholder="Cellulare" value={form.mup_partner.cell} onChange={setMup("cell")} />
                  <Input data-testid="p-indirizzo" placeholder="Indirizzo / sede" value={form.mup_partner.indirizzo} onChange={setMup("indirizzo")} />
                  <Input data-testid="p-recapiti" placeholder="Recapiti (tel / email)" value={form.mup_partner.recapiti} onChange={setMup("recapiti")} />
                  <Input data-testid="p-sito" placeholder="Sito web" value={form.mup_partner.sito} onChange={setMup("sito")} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Mandato di collaborazione firmato (PDF)</Label>
                <div className="flex items-center gap-2">
                  <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={(e) => setMandatoFile(e.target.files[0])} />
                  <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} className="gap-1.5" data-testid="p-mandato-upload">
                    <Upload className="h-4 w-4" /> Carica PDF
                  </Button>
                  {mandatoFile && <span className="flex items-center gap-1 text-xs text-emerald-600"><FileCheck className="h-3.5 w-3.5" /> {mandatoFile.name}</span>}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={create} disabled={saving || !form.email || !form.password || !form.name} data-testid="p-save-button"
                className="bg-terracotta hover:bg-terracotta-hover">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crea e attiva"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="overflow-hidden rounded-xl border-border/60">
        <div className="p-3">
          <BulkBar endpoint="/admin/partners/bulk" selected={sel.selected} onDone={load}
            showArchived={showArchived} onToggleArchived={() => setShowArchived((s) => !s)} entityLabel="partner" />
        </div>
        <Table>
          <TableHeader><TableRow className="bg-sage/50">
            <TableHead className="w-10"><Checkbox checked={partners.length > 0 && sel.count >= partners.length} onCheckedChange={() => sel.toggleAll(partners.map((x) => x.id))} data-testid="select-all" /></TableHead>
            <TableHead>Codice</TableHead><TableHead>Partner</TableHead><TableHead>Ruolo</TableHead>
            <TableHead>Collab.</TableHead><TableHead>RUI</TableHead><TableHead>Incasso</TableHead><TableHead>Stato</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {partners.map((p) => (
              <TableRow key={p.id} data-testid={`partner-row-${p.codice_collab}`}>
                <TableCell><RowCheck id={p.id} selected={sel.isSelected(p.id)} onToggle={sel.toggle} /></TableCell>
                <TableCell className="font-medium text-forest">{p.codice_collab}</TableCell>
                <TableCell>
                  <div className="font-medium text-forest">{p.ragione_sociale}{p.rete_solidale_access && <span className="ml-2 rounded-full bg-sage px-2 py-0.5 text-[10px] font-medium text-forest" data-testid={`partner-rete-${p.codice_collab}`}>Rete Solidale</span>}</div>
                  <div className="text-xs text-muted-foreground">{p.email}</div>
                </TableCell>
                <TableCell className="text-sm">{ROLE_LABELS[p.ruolo_rete]}</TableCell>
                <TableCell className="text-sm">{p.tipo_collab === "A_A" ? "A con A" : "A con B"}</TableCell>
                <TableCell className="text-sm">{p.rui_section} {p.rui_number}</TableCell>
                <TableCell>
                  <button data-testid={`incasso-toggle-${p.codice_collab}`} onClick={() => toggleIncasso(p)}
                    className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors ${p.incasso_diretto ? "border-amber-300 bg-amber-50 text-amber-700" : "border-border/60 bg-muted text-muted-foreground hover:bg-sage/40"}`}>
                    {p.incasso_diretto ? "Diretto Partner" : "Agenzia"}
                  </button>
                </TableCell>
                <TableCell>
                  <span data-testid={`partner-stato-${p.codice_collab}`}
                    className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${(STATO_META[p.stato] || STATO_META.attivo).cls}`}>
                    {(STATO_META[p.stato] || STATO_META.attivo).label}
                  </span>
                </TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-2">
                    <Select value={p.stato || "attivo"} onValueChange={(v) => changeStato(p, v)}>
                      <SelectTrigger data-testid={`stato-select-${p.codice_collab}`} className="h-8 w-[180px] text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATO_ORDER.map((s) => (
                          <SelectItem key={s} value={s} data-testid={`stato-opt-${p.codice_collab}-${s}`}>{STATO_META[s].label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button size="sm" variant="ghost" onClick={() => openProd(p)} data-testid={`prodotti-${p.codice_collab}`} className="gap-1 text-forest">
                      <Boxes className="h-3.5 w-3.5" /> Prodotti
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setDashFor(p)} data-testid={`dashboard-${p.codice_collab}`} className="gap-1 text-forest">
                      <LayoutDashboard className="h-3.5 w-3.5" /> Dashboard
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setResetFor(p)} data-testid={`reset-${p.codice_collab}`} className="gap-1 text-forest">
                      <KeyRound className="h-3.5 w-3.5" /> Reset
                    </Button>
                    <RowActions endpoint="/admin/partners/bulk" id={p.id} archived={showArchived} onDone={load} />
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <DashboardToolsDialog partner={dashFor} onClose={() => setDashFor(null)} onSaved={load} />
      <Dialog open={!!resetFor} onOpenChange={() => setResetFor(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle className="font-heading text-forest">Reset password</DialogTitle></DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Nuova password per {resetFor?.ragione_sociale}</Label>
            <Input data-testid="reset-password-input" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} />
          </div>
          <DialogFooter>
            <Button onClick={doReset} disabled={!newPwd} className="bg-terracotta hover:bg-terracotta-hover">Aggiorna</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!prodFor} onOpenChange={() => setProdFor(null)}>
        <DialogContent className="sm:max-w-md" data-testid="prodotti-dialog">
          <DialogHeader><DialogTitle className="font-heading text-forest">Prodotti abilitati — {prodFor?.ragione_sociale}</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Il Partner vedrà nel menu e potrà quotare SOLO i prodotti selezionati. Gli altri non compaiono e l'accesso via URL è bloccato.</p>
          <div className="max-h-[50vh] space-y-1 overflow-y-auto py-2">
            {PRODUCT_ORDER.map((code) => {
              const cat = PRODUCT_CATALOG[code];
              return (
                <label key={code} data-testid={`prod-check-${code}`} className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-sage/40">
                  <Checkbox checked={prodSel.includes(code)} onCheckedChange={() => toggleProd(code)} />
                  <cat.icon className="h-4 w-4 text-forest/70" />
                  <span className="flex-1 text-sm text-forest">{cat.label}{cat.soon ? " (in arrivo)" : ""}{cat.external ? " (link esterno)" : ""}</span>
                </label>
              );
            })}
          </div>
          <DialogFooter className="flex-row justify-between sm:justify-between">
            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setProdSel(PRODUCT_ORDER)} data-testid="prod-select-all">Tutti</Button>
              <Button type="button" variant="outline" size="sm" onClick={() => setProdSel([])} data-testid="prod-select-none">Nessuno</Button>
            </div>
            <Button onClick={saveProd} disabled={prodSaving} data-testid="prod-save" className="bg-terracotta hover:bg-terracotta-hover">
              {prodSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
